/**
 * What a person reads when something fails (docs/FAILURES.md). Plain words, and always what to do next.
 * The form keeps what was typed in every case; these only say why it isn't saved yet.
 */
import { ApiError } from "../api.ts";

/** Offline or the server couldn't be reached (fetch threw, or ApiError status 0). */
export function isOffline(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 0;
  return e instanceof TypeError; // "Failed to fetch" / "Load failed"
}

/** Worth trying again later without changing anything: offline, timeout, too many, or the server's own failure. */
export function isRetryable(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 0 || e.status === 408 || e.status === 429 || e.status >= 500;
  return isOffline(e);
}

export const NOT_SAVED_TRY_AGAIN = "Not saved yet. Try again.";
export const OFFLINE_NOT_SAVED = "You're offline. Not saved yet. Try again when you're back online.";
export const SESSION_EXPIRED_KEPT = "Your sign-in expired. What you typed is kept on this device: sign in again to save it.";

/** "Your role can't do this. Ask Jordan." (the owner's name from GET /me, when the server sends it). */
export function roleBlockedText(ownerName?: string | null): string {
  return `Your role can't do this. Ask ${ownerName?.trim() || "the owner"}.`;
}

/** The message under a Save button that failed. `ownerName` for the 403 line. */
export function saveErrorText(e: unknown, opts: { ownerName?: string | null } = {}): string {
  if (isOffline(e)) return OFFLINE_NOT_SAVED;
  if (e instanceof ApiError) {
    if (e.status === 401) return SESSION_EXPIRED_KEPT;
    if (e.status === 403) return roleBlockedText(opts.ownerName);
    if (e.status >= 500 || e.status === 408 || e.status === 429) return NOT_SAVED_TRY_AGAIN;
    if (e.status === 404) return "That's gone now (someone may have removed it). Go back and refresh.";
    return e.message || NOT_SAVED_TRY_AGAIN;
  }
  return e instanceof Error && e.message ? e.message : NOT_SAVED_TRY_AGAIN;
}
